import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Header, Grid, Segment, Statistic, Form, Ref } from 'semantic-ui-react';
import { MultipleToggableDropdown } from 'dyl-components';
import { Controller, useForm } from 'react-hook-form';
import { Chart } from 'dyl-components';
import { GroupedBarChart } from 'dyl-components';
import userDashboardActions from 'actions/user_dashboard';
import pbxConfigActions from 'actions/pbx_config';
import { PhoneUtil } from 'utils';

const BarChart = ({ calls_report, maxWidth }) => {
  const list = Array.isArray(calls_report?.user_report)
    ? calls_report.user_report
    : Object.values(calls_report?.user_report || {});
  const data = list.length ? list.flatMap((report) => {
    const userName = `${report.user?.first_name || ''} ${report.user?.last_name || ''}`.trim();
    return [
      { x: userName, y: Number(report.total_calls) || 0, z: "Total Calls" },
      { x: userName, y: Number(report.total_inbound) || 0, z: "Inbound" },
      { x: userName, y: Number(report.total_outbound) || 0, z: "Outbound" }
    ];
  }) : []
  const width = !data?.length ? 1000 : (data?.length + 2) * 50;
  const height = 230
  const margin = { top: 30, right: 50, bottom: 50, left: 60 };
  const colors = {
    "Inbound": "#616BEE",
    "Outbound": "#2A79FF",
    "Total Calls": "#10C6B4"
  };

  return (
    <Segment style={{
      overflowX: "scroll", maxWidth: maxWidth || "100%"
    }} >
      <Chart width={Math.max(maxWidth - 80, width)} height={height} isDynamic>
        <GroupedBarChart
          key={JSON.stringify(data)}
          data={data?.length ? data : null}
          width={width}
          height={height}
          margin={margin}
          colors={colors}
          hoverColors={colors}
          // onMouseOver={(data) => console.log('Hover:', data)}
          maxBarWidth={25}
          boxWidth={Math.max(maxWidth - 60, width)}
          yPadding={0.05}
        />
      </Chart>
    </Segment>
  )
};

export default function CallsByAgent({ start_time, end_time }) {
  const { isReadingCallsReport: loading, calls_report } = useSelector((state) => state.user_dashboard);

  const totalCalls = calls_report?.total?.total_calls || "0"
  const totalOutbound = calls_report?.total?.total_outbound || "0"
  const totalOutboundTime = calls_report?.total?.length_outbound ? PhoneUtil.timeTicker(calls_report?.total?.length_outbound) : "00:00:00"
  const totalInbound = calls_report?.total?.total_inbound || "0"
  const totalInboundTime = calls_report?.total?.lenght_inbound ? PhoneUtil.timeTicker(calls_report?.total?.lenght_inbound) : "00:00:00"

  const [search, setSearch] = useState("");

  const user = useSelector(state => state.auth);

  const { control, watch } = useForm({
    defaultValues: {
      users: [{ value: Number(user?.user_id), name: user?.name }]
    }
  });

  const dispatch = useDispatch()
  const { userExtensions, isReadingUserExtensions } = useSelector(state => state.pbx_config)

  const onSearchChange = (e, { value }) => {
    setSearch(value);
  }

  const usersWithExtensions = userExtensions?.filter((user) => user?.user)?.map(({ user }) => ({
    text: user.first_name + " " + user.last_name,
    key: user.user_id,
    value: Number(user.user_id),
    hasOptions: false,
    options: [],
    parent: null
  }));

  const includesLoggedInUser = usersWithExtensions?.find((userWithExtension) => userWithExtension?.value === Number(user?.user_id)) !== undefined;

  const gridRef = useRef();
  const selectedUsers = watch("users");
  useEffect(() => {
    const user_ids = selectedUsers?.map((user) => user.value);
    dispatch(userDashboardActions.getCallsReport({
      user_ids: user_ids && user_ids.length ? user_ids : undefined,
      start_time,
      end_time
    }))
  }, [dispatch, selectedUsers, start_time, end_time]);

  useEffect(() => {
    dispatch(pbxConfigActions.getUserExtensions({ limit: 2000 }))
  }, [dispatch]);

  return (
    <Segment className="CallsByAgent__Segment" compact loading={loading}>
      <Header as='h3' color="primary">
        Calls by Agent
      </Header>

      <Grid stretched>
        <Grid.Row>
          <Grid.Column width={5}>
            <Grid.Row>
              <Grid.Column>
                <Form>
                  <Controller
                    name='users'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Form.Field
                        control={MultipleToggableDropdown}
                        values={value}
                        nested_options={[...usersWithExtensions, ...(includesLoggedInUser ? [] : [{
                          text: user.name,
                          key: user.user_id,
                          value: user.user_id,
                          hasOptions: false,
                          options: [],
                          parent: null
                        }])].sort((a, b) => a.text > b.text ? 1 : -1)}
                        onChange={(_, { value }) => {
                          setSearch("");
                          onChange({ target: { name, value } });
                        }}
                        placeholder='Search users'
                        onHoverParent={() => { }}
                        searchValue={search}
                        onSearchChange={onSearchChange}
                        loading={isReadingUserExtensions}
                        isExpandable
                        topBorder={false}
                      />
                    )}
                  />
                </Form>
              </Grid.Column>
            </Grid.Row>

            {/* Statistics Layout */}
            <Grid columns={3} className="CallsByAgent__StatsMatrix">
              <Grid.Row>
                <Grid.Column width={5} textAlign='center' verticalAlign='middle'>
                  <Statistic size='mini'>
                    <Statistic.Value className="CallsByAgent__TotalCallsCircle">
                      {totalCalls}
                    </Statistic.Value>
                    <br />
                    <Statistic.Label className="CallsByAgent__StatLabel">Total Calls</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column width={11} className="CallsByAgent__MetricsMatrix">
                  <Grid columns={2}>
                    <Grid.Row verticalAlign='top'>
                      <Grid.Column textAlign='left'>
                        <Statistic size='mini'>
                          <Statistic.Value>{totalOutbound}</Statistic.Value>
                          <br />
                          <Statistic.Label className="CallsByAgent__StatLabel">Total Outbound</Statistic.Label>
                        </Statistic>
                      </Grid.Column>
                      <Grid.Column textAlign='left'>
                        <Statistic size='mini'>
                          <Statistic.Value>{totalOutboundTime}</Statistic.Value>
                          <br />
                          <Statistic.Label className="CallsByAgent__StatLabel">Total Outbound Time</Statistic.Label>
                        </Statistic>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Grid columns={2}>
                    <Grid.Row verticalAlign='bottom'>
                      <Grid.Column textAlign='left'>
                        <Statistic size='mini'>
                          <Statistic.Value>{totalInbound}</Statistic.Value>
                          <br />
                          <Statistic.Label className="CallsByAgent__StatLabel">Total Inbound</Statistic.Label>
                        </Statistic>
                      </Grid.Column>
                      <Grid.Column textAlign='left'>
                        <Statistic size='mini'>
                          <Statistic.Value>{totalInboundTime}</Statistic.Value>
                          <br />
                          <Statistic.Label className="CallsByAgent__StatLabel">Total Inbound Time</Statistic.Label>
                        </Statistic>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Ref innerRef={gridRef}>
            <Grid.Column width={11} >
              <BarChart calls_report={calls_report} maxWidth={gridRef.current?.getBoundingClientRect()
                .width} />
            </Grid.Column>
          </Ref>
        </Grid.Row>
      </Grid>
    </Segment>
  )
}
